/**
 * The record body — the sections (or tabs of sections) below the header, plus
 * the floating save bar.
 *
 * This is the scrollable region's CONTENT, not the scroller: the host's chrome
 * owns the scroll container (`SlideoutMenu.Content` in the slideout and the
 * inline pane, a plain `overflow-y-auto` column in the builder). The header
 * is a sibling region rendered by the chrome (`RecordHeader`), never a child
 * of this component — that separation is what keeps the chrome's exits
 * independent of whether a record resolved (docs/claude/REACT_19_PATTERNS.md
 * § 1).
 *
 * Reads the record and the shared UI state from `RecordViewContext`. Every
 * surface that shows a record — the slideout, the inline pane, builder mode —
 * renders this same component, which is the point: a second implementation
 * of these rows drifts from this one the moment either is touched (see
 * `OverviewFieldRow`, which exists because this exact row geometry had
 * already been duplicated across four files — issue #9195).
 *
 * ## Section manifest
 *
 * `sections` (from the view context) narrows what renders. Every entry
 * defaults to `true`, and each section still ANDs with its own capability
 * check. Narrowing exists for surfaces where a section is not merely
 * undesirable but *invalid* — most importantly a draft record that does not
 * exist on the server, where the timeline, notes, files and research sections
 * would all fire entity-scoped requests for an id the server has never seen.
 */

import { type ReactElement, useMemo, useState } from 'react';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { deriveAutomationCapabilities } from '@/lib/crm/automations/capabilities';

import { SaveBar } from '../components/SaveBar';
import type { SlideoutTabId } from '../record/registry/entityTypeRegistry';
import { getEntityTypeConfig } from '../record/registry/entityTypeRegistry';
import { AUTOMATION_ENTITY_TYPES } from '../record/registry/entityTypeRegistry.automation';
import { useRecordDraft } from './contexts/RecordDraftContext';
import {
  useRecordView,
  useRecordViewActions,
} from './contexts/RecordViewContext';
import { isTaskRecord } from './notes/noteKind';
import { RECORD_BODY_GUTTER } from './record-layout';
import { RecordBodyTabbedContent } from './RecordBodyTabbedContent';
import { automationRunFooterApplies } from './sections/automation-run-footer';
import { DraftScriptSection } from './sections/draft-script-section';
import { FilesSection } from './sections/files-section';
import { NotesSection } from './sections/notes-section';
import { OverviewSection } from './sections/overview-section';
import { RecordMetaSection } from './sections/record-meta-section';
import { ResearchSection } from './sections/research-section';
import { SummarySection } from './sections/summary-section';
import { TasksSection } from './sections/tasks-section';
import { TimelineSection } from './sections/timeline-section';
import { TypeSpecificSection } from './sections/type-specific-section';
import { getEffectiveFixtures } from './utils/fixtures';
import { getEffectiveScriptSource } from './utils/scriptSource';

// The Overview tab holds triggers + the configurable fields; the fields
// section inside it is headed "Settings" (see the OverviewSection title
// override), so the tab stays the broader word and the section names the
// specific thing.
const TAB_LABELS: Record<SlideoutTabId, string> = {
  overview: 'Overview',
  code: 'Code',
  testcases: 'Test Cases',
  runs: 'Runs',
};

export function RecordBody(): ReactElement {
  const {
    object,
    sections: enabled,
    saveBarState,
    overviewEditMode,
    noteCreateRequested,
    notesEnabled,
    tasksEnabled,
  } = useRecordView();
  const draft = useRecordDraft();
  const {
    setSaveBarState,
    setOverviewEditMode,
    requestNoteCreation,
    noteCreateHandled,
  } = useRecordViewActions();

  const entityTypeName = object.entity_type_name;
  const entityId = object.entity_id;
  const orgIdStr = String(object.org_id);

  // Tasks pin a sticky action footer (TaskActionFooter), so the body needs
  // only a short bottom inset.
  const isTask = isTaskRecord(object);
  const isEmailThread = entityTypeName === 'ahoy_email_thread';

  // Per-entity-type overview override (e.g. ahoy_note → NoteRecordOverview).
  // Reading this from the registry instead of branching inline keeps this
  // component entity-type-agnostic — adding a bespoke overview for a new
  // entity type is a one-line registry change in entityTypeRegistry.ts.
  const entityConfig = useMemo(
    () => getEntityTypeConfig(entityTypeName),
    [entityTypeName]
  );
  const OverviewOverride = entityConfig.components?.overview;
  // Section rendered directly above the timeline (e.g. meetings' recurring series).
  const BeforeTimeline = entityConfig.components?.beforeTimeline;
  // Entity types whose bespoke body already presents the record's events
  // (e.g. the email-thread conversation) suppress the generic timeline here;
  // the full-page Activity tab keeps it.
  const showTimeline = enabled.timeline && !entityConfig.hideTimelineSection;
  const typeSpecificBeforeOverview =
    entityConfig.typeSpecificPosition === 'before-overview';

  // ── Tabbed body (slideoutTabs) ─────────────────────────────────────────────
  // Two-zone layout per the automation design: the top zone stays basic
  // (header, sub-header badges) and everything else lives behind tabs.
  // Declared tabs self-gate on availability, and HorizontalTabs hides the bar
  // when only one survives.
  const Documentation = entityConfig.components?.documentation;
  // A pending draft's script and fixtures are projected onto `object` like
  // every other proposed property, so both tabs are reviewable before
  // approval through the same read as a saved record.
  const scriptSource = getEffectiveScriptSource(object);
  const hasTestFixtures = useMemo(
    () =>
      entityConfig.slideoutTabs !== undefined &&
      getEffectiveFixtures(object).length > 0,
    [entityConfig.slideoutTabs, object]
  );
  const slideoutTabs = useMemo(() => {
    const declared = entityConfig.slideoutTabs;
    if (!declared) {
      return undefined;
    }
    return declared.filter(tab => {
      switch (tab) {
        case 'code':
          return scriptSource !== '';
        case 'testcases':
          return hasTestFixtures;
        case 'runs':
          // Respects the surface's timeline suppression — the builder's
          // unsaved-draft panel turns the timeline off, and a Runs tab for a
          // record that does not exist yet would only ever be empty.
          return enabled.timeline;
        default:
          return true;
      }
    });
  }, [
    entityConfig.slideoutTabs,
    scriptSource,
    hasTestFixtures,
    enabled.timeline,
  ]);
  const isTabbed = slideoutTabs !== undefined;

  // Automation-only: the "Settings" section renders the configurable fields,
  // so it is meaningful only when the record carries a non-empty
  // ahoy_config_schema (hasConfig). Script-only / agent-authored automations
  // have no settings to show. The gate applies ONLY to automation entity
  // types — other tabbed records (ahoy_agent) have schema-defined properties,
  // not an ahoy_config_schema, so for them hasConfig is trivially false and
  // gating on it would blank the whole Overview tab (section, fields, AND its
  // Edit affordance). Do NOT apply this to the non-tabbed OverviewSection
  // below either (that serves non-automation records). #12309
  const settingsCaps = useMemo(
    () => deriveAutomationCapabilities(object),
    [object]
  );
  const isAutomationType = entityTypeName in AUTOMATION_ENTITY_TYPES;
  const showSettings =
    enabled.overview && (!isAutomationType || settingsCaps.hasConfig);

  // Drives the scroll column's bottom padding: when the sticky Run-now
  // footer (rendered by the surface's pinned footer slot, not here — the
  // slideout's ReadyFooter in record-slideout-regions.tsx, or the builder
  // panel's frame) is showing, this column needs less clearance than the
  // floating SaveBar's pb-24. Reads the same predicate AutomationRunFooter
  // uses — SaveBar gate included — so the two can never disagree.
  const showRunFooter = useMemo(
    () => automationRunFooterApplies(object, draft, saveBarState),
    [object, draft, saveBarState]
  );

  const [selectedTab, setActiveTab] = useState<string>('overview');
  // A tab can disappear under the user — approving a change that removes the
  // script, for instance. Fall back to Overview rather than rendering no
  // panel at all.
  const activeTab = slideoutTabs?.includes(selectedTab as SlideoutTabId)
    ? selectedTab
    : 'overview';
  const tabItems = useMemo(
    () => (slideoutTabs ?? []).map(id => ({ id, label: TAB_LABELS[id] })),
    [slideoutTabs]
  );

  const isResearchEnabled = useFeatureFlag('research_feature');
  const showResearch =
    enabled.research &&
    isResearchEnabled &&
    (entityTypeName === 'ahoy_contact' || entityTypeName === 'ahoy_company');

  return (
    <>
      {/* `pb-24` reserves vertical clearance for the floating SaveBar so the
          last section's content can scroll above the bar instead of hiding
          behind it when changes are pending. */}
      {isTabbed ? (
        <RecordBodyTabbedContent
          object={object}
          enabled={enabled}
          showRunFooter={showRunFooter}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabItems={tabItems}
          slideoutTabs={slideoutTabs}
          showSettings={showSettings}
          setSaveBarState={setSaveBarState}
          entityId={entityId}
          orgIdStr={orgIdStr}
          entityTypeName={entityTypeName}
          scriptSource={scriptSource}
          Documentation={Documentation}
          recordMetaVariant={entityConfig.recordMeta?.variant}
          recordMetaCreatorAssociationTypes={
            entityConfig.recordMeta?.creatorAssociationTypes
          }
        />
      ) : (
        <div
          className={`${RECORD_BODY_GUTTER} py-5 ${
            // Email threads and tasks have a real sibling footer
            // (not the overlaying SaveBar), so they don't need the tall
            // pb-24 clearance — that just leaves a dead white band above
            // the footer.
            isEmailThread || isTask ? 'pb-4' : 'pb-24'
          } space-y-[15px]`}
        >
          {enabled.summary && entityTypeName !== 'ahoy_reminder' && (
            <SummarySection object={object} />
          )}
          {enabled.typeSpecific && typeSpecificBeforeOverview && (
            <TypeSpecificSection
              object={object}
              onRequestNoteCreation={
                notesEnabled ? requestNoteCreation : undefined
              }
            />
          )}
          {enabled.overview &&
            (OverviewOverride ? (
              // Per-entity-type overview overrides (e.g. NoteRecordOverview)
              // currently bypass the unsaved-changes guard — they don't accept
              // `onSaveStateChange`. The note overview's NoteCard manages its
              // own internal edit lifecycle and submits through useMemoRecords, so
              // closing mid-edit drops only local form state, not in-flight
              // data. If a future override grows true save-bar semantics,
              // extend the override contract to accept `onSaveStateChange`.
              <OverviewOverride
                object={object}
                editMode={overviewEditMode}
                onEditModeChange={setOverviewEditMode}
              />
            ) : (
              <OverviewSection
                object={object}
                onSaveStateChange={setSaveBarState}
              />
            ))}
          {entityConfig.recordMeta && enabled.timeline && (
            <RecordMetaSection
              object={object}
              variant={entityConfig.recordMeta.variant}
              creatorAssociationTypes={
                entityConfig.recordMeta.creatorAssociationTypes
              }
            />
          )}
          {enabled.typeSpecific && !typeSpecificBeforeOverview && (
            <TypeSpecificSection
              object={object}
              onRequestNoteCreation={
                notesEnabled ? requestNoteCreation : undefined
              }
            />
          )}
          {/* Directly after the overview. Shows the record's script — the
              draft's proposed one when a proposal is pending (projected onto
              the object), else the SAVED one. Renders nothing when neither
              exists (rules, built-in automations). */}
          <DraftScriptSection object={object} />
          {notesEnabled && (
            <NotesSection
              orgId={object.org_id}
              entityId={entityId}
              entityTypeName={entityTypeName}
              createRequested={noteCreateRequested}
              onCreateRequestHandled={noteCreateHandled}
            />
          )}
          {tasksEnabled && (
            <TasksSection
              orgId={object.org_id}
              entityId={entityId}
              entityTypeName={entityTypeName}
            />
          )}
          {enabled.files && !entityConfig.hideFilesSection && (
            <FilesSection
              orgId={object.org_id}
              entityId={entityId}
              entityTypeName={entityTypeName}
            />
          )}
          {showResearch && <ResearchSection object={object} />}
          {showTimeline && BeforeTimeline && <BeforeTimeline object={object} />}
          {showTimeline && (
            <TimelineSection
              entityId={entityId}
              orgId={orgIdStr}
              entityType={entityTypeName}
            />
          )}
        </div>
      )}
      {/* Floating SaveBar — overlays the bottom of the surface when pending
          changes exist. It is absolutely positioned against the surface's
          `relative` frame (the slideout dialog, the inline pane's column):
          the host's scroll container is NOT positioned, so the bar's
          containing block is an ancestor of the scroller and the scroller's
          overflow does not clip it. Hidden when nothing is pending; exit
          from edit-mode-without-changes happens via the Cancel action in
          OverviewSection's header. */}
      {saveBarState && (
        <SaveBar
          changeCount={saveBarState.changeCount}
          onSave={saveBarState.onSave}
          onCancel={saveBarState.onCancel}
          isSaving={saveBarState.isSaving}
          error={saveBarState.error}
          blockedByConflicts={saveBarState.blockedByConflicts}
          blockedReason={saveBarState.blockedReason}
          changedFieldLabels={saveBarState.changedFieldLabels}
        />
      )}
    </>
  );
}
